import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationResult, MsalAuthenticationTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Box, CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { lazy, Suspense, useEffect } from 'react';
import ReactGA from 'react-ga';
import { Route, Routes, useLocation } from 'react-router';
import { Navigate } from 'react-router-dom';

import TokenProtectedRoute from 'components/helpcomponents/TokenProtectedRoute';
import { BlkCircularProgress } from './components/helpcomponents/Progress/CircularProgress';
import { ProtectedRoute } from './components/helpcomponents/ProtectedRoute';
import { Layout } from './components/Layout';
import { loginRequest } from './components/remoteData/authProvider';
import { UserGroupProvider } from './contexts/UserGroupContext';
import ErrorBoundary from './error-boundry';
import { blkTheme } from './theme';

import { DRAWER_WIDTH } from 'components/constants';
import { UserGroupName } from './types/user.type';

import './custom.css';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Every query but the two on the notifications page already set this, so
      // make it the default: waking an idle tab should not refetch by itself.
      refetchOnWindowFocus: false,
    },
  },
});

const MermaidDiagram = lazy(() => import('components/admin/MermaidDiagram'));
const ImportOutletPricelist = lazy(() => import('components/import/ImportOutletPricelist'));
const TranslationJobStatus = lazy(() => import('components/import/TranslationJobStatus'));
const EditPermissions = lazy(() => import('components/admin/EditPermissions'));
const FeatureFlagAdministration = lazy(() => import('components/admin/FeatureFlagAdministration'));
const CreateStormImportfile = lazy(() => import('./components/import/CreateStormImportfile'));
const ImportByExcel = lazy(() => import('./components/import/ImportByExcel'));
const UserImportStatus = lazy(() => import('./components/import/UserImportStatus'));
const ImportFromCompany = lazy(() => import('./components/laundry/ImportFromCompany'));
const ImportUsersByEmail = lazy(() => import('./components/laundry/ImportUsersByEmail'));
const ListImportStatus = lazy(() => import('./components/laundry/ListImportStatus'));
const Reports = lazy(() => import('./components/laundry/Reports'));
const EditUser = lazy(() => import('./components/admin/EditUser'));
const EditRoles = lazy(() => import('./components/admin/EditRoles'));
const EditBusinessArea = lazy(() => import('./components/admin/EditBusinessArea'));
const ProductExport = lazy(() => import('./components/export/products'));
const NotFound = lazy(() => import('./components/NotFound'));
const Notifications = lazy(() => import('./components/notifications/notifications.component'));
const Hangtight = lazy(() => import('./components/hangtight/Hangtight'));
const HangtightEvents = lazy(() => import('./components/hangtight/events/HangtightEvents'));
const HangtightRegistrations = lazy(() => import('./components/hangtight/registrations/HangtightRegistrations'));
const HangtightCSVExport = lazy(() => import('./components/hangtight/registrations/HangtightCSVExport'));
const Giveaway = lazy(() => import('./components/giveaway/Giveaway'));
const GiveawayItems = lazy(() => import('./components/giveaway/GiveawayItems'));
const GiveawayItemEditor = lazy(() => import('./components/giveaway/GiveawayItemEditor'));
const GiveawayCodeGroups = lazy(() => import('./components/giveaway/GiveawayCodeGroups'));
const GiveawayCodeGroupEditor = lazy(() => import('./components/giveaway/GiveawayCodeGroupEditor'));
const GiveawayCodes = lazy(() => import('./components/giveaway/GiveawayCodes'));
const GiveawayOrders = lazy(() => import('./components/giveaway/GiveawayOrders'));
const GiveawayD365Settings = lazy(() => import('./components/giveaway/GiveawayD365Settings'));
const GiveawayShopSettings = lazy(() => import('./components/giveaway/GiveawayShopSettings'));
const Home = lazy(() => import('./components/Home'));
const authRequest = {
  ...loginRequest,
};

// Without this, MsalAuthenticationTemplate rethrows the MSAL error and the
// error boundary turns a recoverable token failure into a dead page.
const AuthenticationError: React.FC<MsalAuthenticationResult> = ({ error, login }) => {
  useEffect(() => {
    console.warn('Authentication failed', error);
  }, [error]);

  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h1>YOUR SESSION COULD NOT BE RENEWED</h1>
      <p>{error?.errorCode}</p>
      <button type="button" onClick={() => void login(InteractionType.Redirect, authRequest)}>
        Sign in again
      </button>
    </div>
  );
};
const EuConformity = lazy(() => import('./components/euConformity/FileForm'));
const DeliveryMethods = lazy(() => import('./components/deliveryMethods/DeliveryMethods'));
const DeliveryMethodsList = lazy(() => import('./components/deliveryMethods/deliveryMethodsList/DeliveryMethodsList'));
const DeliveryMethodGroupsList = lazy(
  () => import('./components/deliveryMethods/deliveryMethodGroups/DeliveryMethodGroupsList')
);
const DeliveryMethodGroupCompanyAssociations = lazy(
  () => import('./components/deliveryMethods/deliveryMethodGroups/DeliveryMethodGroupCompanyAssociations')
);
const ResetPassword = lazy(() => import('./components/customers/ResetPassword'));
const SizeGroups = lazy(() => import('./components/sizeGroups/SizeGroups'));
const SizeGroupsList = lazy(() => import('./components/sizeGroups/sizeGroupsList/SizeGroupsList'));
const NorceManagement = lazy(() => import('./components/norce/NorceManagement'));

const App: React.FC = () => {
  useEffect(() => {
    ReactGA.initialize('G-X9SBSGYK3H');
    ReactGA.pageview('App');
  }, []);

  const location = useLocation();
  const PUBLIC_ROUTES = ['/admin/mermaid'];
  const isPublicRoute = PUBLIC_ROUTES.some(path => location.pathname.toLowerCase().startsWith(path));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledEngineProvider injectFirst>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={blkTheme}>
            <CssBaseline />
            <UserGroupProvider>
              <Layout>
                <ErrorBoundary resetKey={location.pathname}>
                  <Suspense
                    fallback={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '200px',
                        }}
                      >
                        <BlkCircularProgress />
                      </div>
                    }
                  >
                    {isPublicRoute ? (
                      <Routes>
                        <Route
                          path="/admin/mermaid"
                          element={
                            <TokenProtectedRoute routeName="mermaid">
                              <MermaidDiagram />
                            </TokenProtectedRoute>
                          }
                        />
                      </Routes>
                    ) : (
                      <MsalAuthenticationTemplate
                        interactionType={InteractionType.Redirect}
                        authenticationRequest={authRequest}
                        errorComponent={AuthenticationError}
                      >
                        <Routes>
                          <Route path="/" element={<Home />} />

                          <Route path="/admin">
                            <Route index element={<Navigate to="/admin/users" />} />
                            <Route
                              path="users"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Admin}>
                                  <EditUser />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="roles"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Admin}>
                                  <EditRoles />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="businessareas"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Admin}>
                                  <EditBusinessArea />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="permissions"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Admin} extendedPermission="permissions">
                                  <EditPermissions />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="featureflags"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Admin}>
                                  <FeatureFlagAdministration />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route path="/export">
                            <Route index element={<Navigate to="/export/products" />} />
                            <Route
                              path="products"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Export}>
                                  <ProductExport />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route path="/import">
                            <Route index element={<Navigate to="/import/importbyexcel" />} />
                            <Route
                              path="importbyexcel"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Import}>
                                  <ImportByExcel />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="userimportstatus"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Import}>
                                  <UserImportStatus />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="createstormimportfile"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Import}>
                                  <CreateStormImportfile />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="outletpricelist"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Import} extendedPermission="outlet-import">
                                  <ImportOutletPricelist />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="translationstatus"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Import} extendedPermission="translation-status">
                                  <TranslationJobStatus />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route path="/laundry">
                            <Route index element={<Navigate to="/laundry/importbyemail" />} />
                            <Route
                              path="importbyemail"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Laundry}>
                                  <ImportUsersByEmail />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="importfromcompany"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Laundry}>
                                  <ImportFromCompany />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="listimportstatus"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Laundry}>
                                  <ListImportStatus />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="reports"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Laundry}>
                                  <Reports />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route path="/hangtight"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.Wellhung}>
                                <Hangtight />
                              </ProtectedRoute>
                            }
                          >
                            <Route index element={<Navigate to="/hangtight/events" />} />
                            <Route path="events" element={<HangtightEvents />} />
                            <Route path="registrations" element={<HangtightRegistrations />} />
                            <Route path="export" element={<HangtightCSVExport />} />
                          </Route>

                          <Route path="/giveaway"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.Giveaway}>
                                <Giveaway />
                              </ProtectedRoute>
                            }
                          >
                            <Route index element={<Navigate to="/giveaway/items" />} />
                            <Route path="items" element={<GiveawayItems />} />
                            <Route path="items/new" element={<GiveawayItemEditor />} />
                            <Route path="items/:id" element={<GiveawayItemEditor />} />
                            <Route path="code-groups" element={<GiveawayCodeGroups />} />
                            <Route path="code-groups/new" element={<GiveawayCodeGroupEditor />} />
                            <Route path="code-groups/:id" element={<GiveawayCodeGroupEditor />} />
                            <Route path="codes" element={<GiveawayCodes />} />
                            <Route path="orders" element={<GiveawayOrders />} />
                            <Route path="d365-settings" element={<GiveawayD365Settings />} />
                            <Route path="shop-settings/login" element={<GiveawayShopSettings />} />
                          </Route>

                          <Route path="/delivery-methods"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.DeliveryMethods}>
                                <DeliveryMethods />
                              </ProtectedRoute>
                            }
                          >
                            <Route index element={<Navigate to="/delivery-methods/list" />} />
                            <Route
                              path="list"
                              element={
                                <ProtectedRoute
                                  requiredGroup={UserGroupName.DeliveryMethods}
                                  extendedPermission="delivery-methods"
                                >
                                  <DeliveryMethodsList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="groups"
                              element={
                                <ProtectedRoute
                                  requiredGroup={UserGroupName.DeliveryMethods}
                                  extendedPermission="delivery-method-groups"
                                >
                                  <DeliveryMethodGroupsList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="company-associations"
                              element={
                                <ProtectedRoute
                                  requiredGroup={UserGroupName.DeliveryMethods}
                                  extendedPermission="delivery-method-groups"
                                >
                                  <DeliveryMethodGroupCompanyAssociations />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route
                            path="/size-groups"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.Products} extendedPermission="size-groups">
                                <SizeGroups />
                              </ProtectedRoute>
                            }
                          >
                            <Route index element={<Navigate to="/size-groups/list" />} />
                            <Route path="list" element={<SizeGroupsList />} />
                          </Route>

                          {/* Standalone Routes */}
                          <Route path="/notifications"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.Notifications}>
                                <Notifications />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="/euconformity"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.EuConformity}>
                                <EuConformity />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="/norce"
                            element={
                              <ProtectedRoute requiredGroup={UserGroupName.NorceManagement}>
                                <NorceManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="/customers">
                            <Route index element={<Navigate to="/customers/reset-password" />} />
                            <Route
                              path="reset-password"
                              element={
                                <ProtectedRoute requiredGroup={UserGroupName.Customers}>
                                  <ResetPassword />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </MsalAuthenticationTemplate>
                    )}
                  </Suspense>
                </ErrorBoundary>
              </Layout>
            </UserGroupProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </StyledEngineProvider>
      <UnauthenticatedTemplate>
        <Box textAlign="center" width="100%" p={2} paddingLeft={{ xs: 0, sm: `${DRAWER_WIDTH}px` }}>
          You do not have access. Redirecting to login...
        </Box>
      </UnauthenticatedTemplate>
    </LocalizationProvider>
  );
};

export default App;
